import React from 'react';
import {
  FaUser, FaMapMarkerAlt, FaEnvelope, FaPhone, FaGraduationCap,
  FaBriefcase, FaUsers, FaHeart, FaMoneyBill, FaCalendarAlt,
  FaVenusMars, FaPalette, FaRulerVertical, FaWeight, FaCheckCircle,
  FaClock, FaExclamationTriangle, FaChevronRight, FaChartBar
} from 'react-icons/fa';
import { imageUrl } from '../../utils/imageUrl';

/* ============ MAIN RENDERER ============ */
export default function ResponseRenderer({ render }) {
  if (!render || !render.type) return null;

  switch (render.type) {
    case 'profile-card':  return <ProfileCard profile={render.data} />;
    case 'users-list':    return <UsersList data={render.data} />;
    case 'payments-list': return <PaymentsList data={render.data} />;
    case 'alerts-list':   return <AlertsList data={render.data} />;
    case 'matchings-list':return <MatchingsList data={render.data} />;
    case 'breakdown':     return <Breakdown data={render.data} />;
    case 'table':         return <GenericTable data={render.data} />;
    default:              return null;
  }
}

/* ============ PROFILE CARD ============ */
function ProfileCard({ profile }) {
  const p = profile || {};
  const initial = (p.name || 'U').charAt(0).toUpperCase();
  const isPaid = String(p.payStatus || '').toLowerCase() === 'paid';
  const img = imageUrl(p.image);

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md">
      <div className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
        <div className="relative flex items-center gap-4">
          {img ? (
            <img
              src={img}
              alt={p.name}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow-lg"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-white/30 backdrop-blur-sm flex items-center justify-center text-white text-2xl font-bold border-2 border-white/40 shadow-lg">
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-lg truncate">{p.name || '—'}</h3>
            <p className="text-white/80 text-xs font-mono">ID {p.id}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                isPaid ? 'bg-green-300 text-green-900' : 'bg-yellow-300 text-yellow-900'
              }`}>
                {isPaid ? '✓ PAID' : '⏳ PENDING'}
              </span>
              {p.gender && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-white/25 text-white capitalize">
                  {p.gender}
                </span>
              )}
              {p.age && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-white/25 text-white">
                  {p.age} yrs
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <Section title="Personal" icon={FaUser}>
          <Row label="Gender"  value={cap(p.gender)} />
          <Row label="DOB"     value={p.dob} />
          <Row label="Marital" value={cap(p.marital)} />
          <Row label="Caste"   value={p.caste} />
          <Row label="Gotra"   value={p.gotra} />
        </Section>

        <Section title="Contact" icon={FaPhone}>
          <Row label="Phone"    value={p.contact} icon={FaPhone} />
          <Row label="Email"    value={p.email} icon={FaEnvelope} />
          <Row label="Location" value={[p.city, p.state, p.country].filter(Boolean).join(', ')} icon={FaMapMarkerAlt} />
          <Row label="Residence" value={cap(p.residence)} />
        </Section>

        <Section title="Education & Career" icon={FaGraduationCap}>
          <Row label="Education"   value={p.education} />
          <Row label="Occupation"  value={p.occupation} icon={FaBriefcase} />
          <Row label="Income"      value={p.income} />
          <Row label="Designation" value={p.designation} />
          <Row label="Works At"    value={p.employeeIn} />
        </Section>

        <Section title="Family" icon={FaUsers}>
          <Row label="Father" value={p.father && p.fatherOccupation ? `${p.father} (${p.fatherOccupation})` : p.father} />
          <Row label="Mother" value={p.mother && p.motherOccupation ? `${p.mother} (${p.motherOccupation})` : p.mother} />
          <Row label="Brothers" value={[p.brothers?.married, p.brothers?.unmarried].filter(Boolean).join(' · ') || null} />
          <Row label="Sisters"  value={[p.sisters?.married, p.sisters?.unmarried].filter(Boolean).join(' · ') || null} />
        </Section>

        <Section title="Appearance" icon={FaRulerVertical}>
          <Row label="Height"     value={p.height} />
          <Row label="Weight"     value={p.weight} />
          <Row label="Skin Tone"  value={cap(p.skinTone)} />
        </Section>

        {p.partner && (p.partner.lookingFor || p.partner.age || p.partner.education) && (
          <Section title="Partner Preference" icon={FaHeart} tone="pink">
            <Row label="Looking For" value={p.partner.lookingFor} />
            <Row label="Age"         value={p.partner.age} />
            <Row label="Marital"     value={p.partner.marital} />
            <Row label="Education"   value={p.partner.education} />
            <Row label="Occupation"  value={p.partner.occupation} />
            <Row label="Location"    value={p.partner.location} />
          </Section>
        )}

        {p.aboutMe && (
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">About</p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{p.aboutMe}</p>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-gray-400 pt-2 border-t border-gray-100">
          <FaCalendarAlt />
          Registered {p.registeredAt ? new Date(p.registeredAt).toLocaleString() : '—'}
        </div>
      </div>
    </div>
  );
}

/* ============ SECTION / ROW ============ */
function Section({ title, icon: Icon, children, tone = 'indigo' }) {
  const tones = {
    indigo: 'text-indigo-600 bg-indigo-50',
    pink:   'text-pink-600 bg-pink-50',
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[tone] || tones.indigo}`}>
          <Icon className="text-xs" />
        </div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 pl-9">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="text-gray-300 mt-1 flex-shrink-0 text-xs" />}
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-gray-800 font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

/* ============ USERS LIST ============ */
function UsersList({ data }) {
  const users = data?.users || [];
  if (users.length === 0) return <Empty label="No users found." />;

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <FaUsers className="text-xs" />
          </div>
          <p className="text-sm font-semibold text-gray-800">
            {data.filter === 'paid' ? 'Paid Users' : 'Users'} · {users.length}
          </p>
        </div>
      </div>

      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {users.map((u, i) => {
          const isPaid = String(u.payStatus || '').toLowerCase() === 'paid';
          const img = imageUrl(u.image);
          return (
            <li key={u.id || i} className="px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
              {img ? (
                <img
                  src={img}
                  alt={u.name}
                  onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('div'), {
                    className: 'w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0',
                    textContent: (u.name || 'U').charAt(0).toUpperCase(),
                  })); }}
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(u.name || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{u.name || '—'}</p>
                <p className="text-xs text-gray-400 truncate flex items-center gap-2 flex-wrap">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">#{u.id}</span>
                  {u.gender && <span className="capitalize">{u.gender}</span>}
                  {u.city && <span className="flex items-center gap-0.5"><FaMapMarkerAlt className="text-[9px]" /> {u.city}</span>}
                </p>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ${
                isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {isPaid ? '✓ Paid' : '⏳ Pending'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ============ PAYMENTS LIST ============ */
function PaymentsList({ data }) {
  const payments = data?.payments || [];
  if (payments.length === 0) return <Empty label="No payments found." />;

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <FaMoneyBill className="text-xs" />
        </div>
        <p className="text-sm font-semibold text-gray-800">Recent Payments · {payments.length}</p>
      </div>
      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {payments.map((p, i) => {
          const status = String(p.status || '').toLowerCase();
          const color = status === 'completed' ? 'green' : status === 'pending' ? 'yellow' : 'red';
          return (
            <li key={p.id || i} className="px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold text-xs flex-shrink-0">
                NPR
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">NPR {Number(p.amount || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">User #{p.userId}</span>
                  <span>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</span>
                </p>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full bg-${color}-100 text-${color}-700`}>
                {p.status || '—'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ============ ALERTS LIST ============ */
function AlertsList({ data }) {
  const alerts = data?.alerts || [];
  if (alerts.length === 0) return <Empty label="No alerts." />;

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
          <FaExclamationTriangle className="text-xs" />
        </div>
        <p className="text-sm font-semibold text-gray-800">Alerts · {alerts.length}</p>
      </div>
      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {alerts.map((a, i) => {
          const color = a.severity === 'high' ? 'red' : a.severity === 'medium' ? 'yellow' : 'green';
          return (
            <li key={a.id || i} className="px-4 py-3 hover:bg-gray-50 flex items-start gap-3">
              <span className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 bg-${color}-500`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800">{a.message}</p>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{a.type}</span>
                  <span>{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</span>
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ============ MATCHINGS LIST ============ */
function MatchingsList({ data }) {
  const matchings = data?.matchings || [];
  if (matchings.length === 0) return <Empty label="No matchings." />;

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center">
          <FaHeart className="text-xs" />
        </div>
        <p className="text-sm font-semibold text-gray-800">
          Matchings · User #{data.userId}
        </p>
      </div>
      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {matchings.map((m, i) => {
          const img = imageUrl(m.matchedImage);
          return (
            <li key={m.id || i} className="px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
              {img ? (
                <img
                  src={img}
                  alt={m.matchedName}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(m.matchedName || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{m.matchedName || '—'}</p>
                <p className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">#{m.matchedUserId}</span>
                  {m.matchedGender && <span className="capitalize">{m.matchedGender}</span>}
                  {m.matchedCity && <span>· {m.matchedCity}</span>}
                </p>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ${
                String(m.status).toLowerCase() === 'pending'
                  ? 'bg-yellow-100 text-yellow-700'
                  : String(m.status).toLowerCase() === 'accepted'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
              }`}>
                {m.status || '—'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ============ BREAKDOWN ============ */
function Breakdown({ data }) {
  const items = data?.items || [];
  if (items.length === 0) return <Empty label="No data." />;
  const max = Math.max(...items.map(i => i.count || 0), 1);

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <FaChartBar className="text-xs" />
        </div>
        <p className="text-sm font-semibold text-gray-800 capitalize">{data.title}</p>
      </div>
      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {items.map((item, i) => {
          const pct = Math.round((item.count / max) * 100);
          return (
            <li key={i} className="px-4 py-2.5 hover:bg-gray-50">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm text-gray-800 font-medium truncate">{item.label || '—'}</p>
                <p className="text-sm font-bold text-indigo-600 ml-3">{Number(item.count).toLocaleString()}</p>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ============ GENERIC TABLE ============ */
function GenericTable({ data }) {
  const rows = data?.rows || [];
  if (rows.length === 0) return <Empty label="No results." />;
  const headers = Object.keys(rows[0]);

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {headers.map(h => (
                <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {headers.map(h => (
                  <td key={h} className="px-3 py-2 text-gray-700 truncate max-w-[200px]">
                    {String(row[h] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ SHARED ============ */
function Empty({ label }) {
  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-2xl px-4 py-6 text-center shadow-sm">
      <div className="w-10 h-10 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-2">
        <FaCheckCircle className="text-gray-400" />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function cap(s) {
  if (!s) return null;
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
}